import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { ReviewStrip } from "@/components/review-strip";
import {
  FEES,
  FIRM,
  FIRM_ADDRESS,
  faqSchema,
} from "@/lib/firm";

const GBP_URL = "https://maps.google.com/?cid=17645464718929886346";
const CANONICAL = "https://coastsidelaw.com.au/conveyancing-near-me";

/** The four suburbs this page is built to win in the local pack. */
const AREAS = [
  {
    slug: "golden-beach",
    name: "Golden Beach",
    postcode: "4551",
    drive: "Our office is on Theresa Street, in the suburb itself",
    note: "Older beachfront houses, canal-front homes on the Pumicestone Passage and a steady flow of unit sales. Flood overlay, esplanade setbacks and canal revetment walls are the checks that matter here.",
  },
  {
    slug: "caloundra",
    name: "Caloundra",
    postcode: "4551",
    drive: "5 minutes north of the Golden Beach office",
    note: "High-rise and low-rise unit schemes dominate. Body corporate records, sinking fund levels and any building-defect history are worth reading before you commit, not after.",
  },
  {
    slug: "pelican-waters",
    name: "Pelican Waters",
    postcode: "4551",
    drive: "6 minutes south-west",
    note: "Canal-front and golf-course homes, plus newer estate stages. Watch covenant and building-covenant conditions, marina berth inclusions and any lot still subject to registration of the plan.",
  },
  {
    slug: "baringa",
    name: "Baringa & Aura",
    postcode: "4551",
    drive: "12 minutes west",
    note: "New builds, house-and-land and off-the-plan contracts in the Aura master plan. Sunset dates, developer variation clauses and the disclosure attached to the contract are the pressure points.",
  },
] as const;

const FAQS = [
  {
    q: "Do you have a conveyancing office near Golden Beach and Caloundra?",
    a: `Yes. Our office is at ${FIRM_ADDRESS}, in Golden Beach itself, a few minutes from Caloundra, Pelican Waters and the Aura estates at Baringa. Appointments are available in person, by phone or by video.`,
  },
  {
    q: "How much is conveyancing in the Caloundra area?",
    a: `A fixed professional fee of ${FEES.sale} incl. GST for a residential sale and ${FEES.purchase} incl. GST for a residential purchase, in all four suburbs. Searches, Titles Office registration fees and transfer duty are additional third-party costs and we set them out in writing before you engage us.`,
  },
  {
    q: "Can I get a contract checked before I sign?",
    a: "Yes, and at no charge. Send the contract through and we will read it, tell you what the special conditions actually do and what to change. Once you sign, your options narrow to the cooling-off period and whatever is already in the document.",
  },
  {
    q: "Are you a conveyancer or a solicitor?",
    a: "A law practice. Queensland does not licence non-lawyer conveyancers the way New South Wales and Victoria do, so property transfers here are legal work. It matters when a file turns awkward — a caveat, a deceased estate on the title, a family transfer with a duty question, a contract that has to be terminated.",
  },
  {
    q: "What are your hours?",
    a: FIRM.hours,
  },
];

const localPackSchema = {
  "@context": "https://schema.org",
  "@type": ["LegalService", "Attorney"],
  "@id": `${CANONICAL}#localpack`,
  name: FIRM.name,
  url: CANONICAL,
  description: `Fixed-fee conveyancing solicitors serving Golden Beach, Caloundra, Pelican Waters and Baringa QLD 4551. Sales ${FEES.sale}, purchases ${FEES.purchase} incl. GST.`,
  telephone: FIRM.phone,
  email: FIRM.email,
  priceRange: "$$",
  currenciesAccepted: "AUD",
  sameAs: [GBP_URL, "https://www.linkedin.com/company/143455959/"],
  address: {
    "@type": "PostalAddress",
    streetAddress: FIRM.street,
    addressLocality: FIRM.suburb,
    addressRegion: FIRM.state,
    postalCode: FIRM.postcode,
    addressCountry: FIRM.country,
  },
  geo: { "@type": "GeoCoordinates", latitude: -26.8175, longitude: 153.1289 },
  hasMap: GBP_URL,
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:30",
      closes: "17:00",
    },
  ],
  areaServed: AREAS.map((a) => ({
    "@type": "City",
    name: a.name,
    address: {
      "@type": "PostalAddress",
      addressLocality: a.name,
      addressRegion: "QLD",
      postalCode: a.postcode,
      addressCountry: "AU",
    },
  })),
  makesOffer: [
    {
      "@type": "Offer",
      name: "Residential conveyancing — sale",
      priceCurrency: "AUD",
      price: "990",
      description: "Fixed professional fee inclusive of GST. Disbursements additional.",
    },
    {
      "@type": "Offer",
      name: "Residential conveyancing — purchase",
      priceCurrency: "AUD",
      price: "1499",
      description: "Fixed professional fee inclusive of GST. Disbursements additional.",
    },
  ],
};

const areaListSchema = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Conveyancing service areas — Caloundra region",
  itemListElement: AREAS.map((a, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: `Conveyancing ${a.name}`,
    url: `https://coastsidelaw.com.au/conveyancing/${a.slug}`,
  })),
};

export const Route = createFileRoute("/conveyancing-near-me")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary" },
      { title: fitTitle("Conveyancing Near Me | Caloundra & Golden Beach QLD 4551") },
      {
        name: "description",
        content: fitDescription(`Conveyancing solicitor near you in Caloundra, Golden Beach, Pelican Waters and Baringa. Office at ${FIRM_ADDRESS}. Fixed fees ${FEES.sale} sale, ${FEES.purchase} purchase incl. GST, free contract review. Call ${FIRM.phone}.`),
      },
      {
        name: "keywords",
        content:
          "conveyancing near me, conveyancer near me Caloundra, conveyancing solicitor Caloundra, conveyancing Golden Beach, conveyancing Pelican Waters, conveyancing Baringa, property lawyer Caloundra QLD 4551",
      },
      {
        property: "og:title",
        content: fitTitle("Conveyancing Near Me — Caloundra, Golden Beach, Pelican Waters & Baringa"),
      },
      {
        property: "og:description",
        content: fitDescription(`Fixed-fee conveyancing from a Golden Beach law office. Free contract review, PEXA settlement, ${FEES.sale} sale / ${FEES.purchase} purchase incl. GST.`),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(localPackSchema) },
      { type: "application/ld+json", children: JSON.stringify(areaListSchema) },
      { type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) },
    ],
  }),
  component: LocalPackPage,
});

function LocalPackPage() {
  return (
    <>
      <PageHero
        eyebrow="Local conveyancing — postcode 4551"
        title="Conveyancing near you: Golden Beach, Caloundra, Pelican Waters and Baringa"
        lead={`A law office on Theresa Street, Golden Beach — not a call centre. Fixed fees of ${FEES.sale} for a sale and ${FEES.purchase} for a purchase, both inclusive of GST, with your contract read before you sign at no charge.`}
      />

      <section className="mx-auto max-w-3xl px-5 py-14">
        <div className="rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Find us</h2>
          <dl className="mt-5 grid gap-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="font-medium text-foreground">Office</dt>
              <dd className="mt-1 text-muted-foreground">{FIRM_ADDRESS}</dd>
            </div>
            <div>
              <dt className="font-medium text-foreground">Phone</dt>
              <dd className="mt-1">
                <a href={FIRM.phoneHref} className="font-semibold text-primary underline">
                  {FIRM.phone}
                </a>
              </dd>
            </div>
            <div>
              <dt className="font-medium text-foreground">Email</dt>
              <dd className="mt-1">
                <a href={FIRM.emailHref} className="font-semibold text-primary underline">
                  {FIRM.email}
                </a>
              </dd>
            </div>
            <div>
              <dt className="font-medium text-foreground">Hours</dt>
              <dd className="mt-1 text-muted-foreground">{FIRM.hours}</dd>
            </div>
          </dl>
          <p className="mt-5 text-sm leading-relaxed text-muted-foreground">
            {FIRM.membership}.{" "}
            <a href={GBP_URL} className="font-semibold text-primary underline" rel="noopener">
              View us on Google Maps
            </a>{" "}
            or{" "}
            <Link to="/reviews" className="font-semibold text-primary underline">
              read client reviews
            </Link>
            .
          </p>
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">The suburbs we cover from here</h2>
        <p className="measure mt-3 text-sm leading-relaxed text-muted-foreground">
          Every one of these is a short drive from the office, and each has its own quirks on title.
        </p>

        <div className="mt-8 grid gap-5 sm:grid-cols-2">
          {AREAS.map((area) => (
            <div key={area.slug} className="rounded-sm border border-border p-6">
              <h3 className="font-serif text-xl text-foreground">
                Conveyancing {area.name} {area.postcode}
              </h3>
              <p className="mt-1 text-xs uppercase tracking-wide text-accent-foreground/70">
                {area.drive}
              </p>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{area.note}</p>
              <Link
                to="/conveyancing/$suburb"
                params={{ suburb: area.slug }}
                className="mt-4 inline-block text-sm font-semibold text-primary underline"
              >
                {area.name} conveyancing details
              </Link>
            </div>
          ))}
        </div>

        <h2 className="mt-14 font-serif text-3xl text-foreground">What the fixed fee covers</h2>
        <ul className="mt-6 space-y-3 text-sm leading-relaxed text-muted-foreground">
          {[
            "Reading the contract and the seller disclosure statement before you sign — no charge",
            "Diarising and chasing cooling-off, building and pest, finance and settlement dates",
            "Title, plan, rates, water, body corporate and state searches, reviewed rather than filed",
            "Settlement statement and adjustments, checked line by line",
            "Electronic settlement through PEXA, with a call the moment it is done",
          ].map((item) => (
            <li key={item} className="border-l-2 border-accent pl-4">
              {item}
            </li>
          ))}
        </ul>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Questions we get asked locally</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 pb-4">
        <EnquiryForm heading="Get a fixed-fee quote for your 4551 property" />
      </section>

      <section className="mx-auto max-w-3xl px-5">
        <ReviewStrip suburb="Golden Beach" />
      </section>

      <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} />

      <CtaBand
        title="Buying or selling around Caloundra?"
        body="Send the contract through before you sign. We will read it at no charge and give you the fixed fee in writing."
      />
      <div className="pb-20" />
    </>
  );
}
